/**
 * RUI input helpers (PR-ONBOARDING-RUI-LOOKUP-UX-INPUT-1). Pure, no IO.
 *
 * The UI collects only the 9 numeric digits of a RUI number; the section
 * letter (A/B/E) is derived from the intermediary org_type and prepended to
 * form the BE-canonical number (e.g. BROKER + "000722648" → "B000722648").
 * The BE contract (and matching/cache) are unchanged — they still receive the
 * canonical, composed at the FE egress points (lookup + submit).
 */

/** Map an intermediary org_type to its RUI section letter; null otherwise. */
export function deriveRuiPrefix(
  orgType: string | null | undefined,
): 'A' | 'B' | 'E' | null {
  switch (orgType) {
    case 'AGENZIA':
      return 'A';
    case 'BROKER':
      return 'B';
    case 'INTERMEDIARIO':
      return 'E';
    default:
      return null;
  }
}

/**
 * Keep only digits and cap at 9. A pasted canonical ("B000722648") or noisy
 * input ("abc000722648xxx") collapses to the 9-digit core ("000722648"); more
 * than 9 digits are truncated to the first 9.
 */
export function normalizeRuiDigits(raw: string | null | undefined): string {
  return (raw ?? '').replace(/\D/g, '').slice(0, 9);
}

/** Valid when exactly 9 digits. */
export function isValidRuiDigits(value: string | null | undefined): boolean {
  return /^\d{9}$/.test((value ?? '').trim());
}

/**
 * Compose the BE-canonical RUI from the 9 numeric digits + derived section.
 * Returns "" when there are no digits. If the section can't be derived (no
 * org_type yet) the bare digits are returned — the BE still canonicalizes a
 * 9-digit number against the derived section.
 */
export function composeCanonicalRui(
  orgType: string | null | undefined,
  digits: string | null | undefined,
): string {
  const d = normalizeRuiDigits(digits);
  if (d === '') return '';
  const prefix = deriveRuiPrefix(orgType);
  return prefix ? `${prefix}${d}` : d;
}
